import { Box, Button, Group, LoadingOverlay, Modal, Text } from '@mantine/core'
import { FormattedMessage, useIntl } from 'react-intl'
import { extractErrorMessage } from '@/api/helpers/extractErrorMessage'
import { useDeleteCamera } from '@/queries/cameraQueries'
import { showToast } from '@/theme/notifications'
import { CameraListItem } from '@/types/camera'

type DeleteCameraModalProps = {
  opened: boolean
  camera?: CameraListItem
  onClose: () => void
  onDelete: () => void
}

export const DeleteCameraModal = ({
  opened,
  camera,
  onClose,
  onDelete
}: DeleteCameraModalProps) => {
  const intl = useIntl()

  const { mutateAsync: deleteCamera, isPending } = useDeleteCamera()

  const handleDeleteCamera = async () => {
    if (!camera) {
      return
    }

    try {
      await deleteCamera({
        deviceId: camera.device_id,
        cameraId: camera.camera_id
      })

      showToast(intl.formatMessage({ id: 'cameras.delete.success' }), 'success')
      onDelete()
    } catch (err) {
      showToast(
        extractErrorMessage(
          err,
          intl.formatMessage({ id: 'cameras.delete.error' })
        ),
        'error'
      )
    }
  }

  return (
    <Modal
      size="lg"
      title={<FormattedMessage id="cameras.delete" />}
      opened={opened}
      onClose={onClose}
    >
      <Box pos="relative">
        <LoadingOverlay visible={isPending} />

        {camera && (
          <Text size="sm" mb="xl">
            <FormattedMessage
              id="cameras.delete.confirmation"
              values={{
                cameraName: camera.camera_name
              }}
            />
          </Text>
        )}

        <Group justify="end">
          <Button miw={120} variant="default" onClick={onClose}>
            <FormattedMessage id="cancel" />
          </Button>

          <Button
            miw={120}
            color="red"
            onClick={() => void handleDeleteCamera()}
          >
            <FormattedMessage id="delete" />
          </Button>
        </Group>
      </Box>
    </Modal>
  )
}
